import React, { Fragment, useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
    Box,
    Stack,
    Typography,
    Paper,
    Chip,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    CircularProgress,
    Alert,
    Tooltip,
    IconButton,
    Collapse,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Tabs,
    Tab,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CreateApi from './Settings/appSettings';
import { useMCContext } from './contexts/useMCStates';
import type {
    MomentUser,
    RewardBucket,
    RewardTransaction,
    TourRewardBucket,
    UserRewards,
    UserCreditsDiscounts,
    CreditDiscountBucket,
    TourCreditDiscountBucket,
    CreditEntry,
    CreditTransaction,
    DiscountEntry,
} from '@moment-golf/types';

const CARD_SX = {
    p: 1.5,
    borderRadius: 2,
    width: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-start',
    boxSizing: 'border-box' as const,
};

/** Bordered accordion for per-tour subsections (Rewards / Discounts / Credits). */
const ACCORDION_SX = {
    width: '100%',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 2,
    '&:before': { display: 'none' },
    boxShadow: 'none',
    '&.Mui-expanded': { margin: 0 },
};

const USAGE_LIMIT_CELL_SX = { minWidth: 50, whiteSpace: 'nowrap' as const };

const EMPTY_MG_BUCKET: RewardBucket = {
    currentBalance: 0,
    lifetimeEarned: 0,
    lifetimeRedeemed: 0,
    transactions: [],
};

const EMPTY_CREDIT_BUCKET: CreditDiscountBucket = { credits: [], discounts: [] };

const DEFAULT_REWARDS: UserRewards = {
    tour: [],
    momentGolf: { ...EMPTY_MG_BUCKET },
};

const DEFAULT_CREDITS_DISCOUNTS: UserCreditsDiscounts = {
    tour: [],
    momentGolf: { ...EMPTY_CREDIT_BUCKET },
};

const formatCurrency = (val: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);

const formatDateTimeLocal = (val: unknown) => {
    if (!val) return '—';
    try {
        return new Date(val as string).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return String(val);
    }
};

const formatDate = (val: unknown) => {
    if (!val) return '—';
    try {
        return new Date(val as string).toLocaleDateString('en-US', { timeZone: 'UTC' });
    } catch {
        return String(val);
    }
};

const formatDateUTC = (val: unknown) => {
    if (!val) return '—';
    try {
        return new Date(val as string).toLocaleDateString('en-US', { timeZone: 'UTC' });
    } catch {
        return '—';
    }
};

const creditGrantedFaceTotal = (entry: CreditEntry): number => {
    const txs = entry.transactions ?? [];
    let sum = 0;
    let counted = false;
    for (const t of txs) {
        if (t.type === 'issued' || t.type === 'adjusted') {
            sum += t.amount;
            counted = true;
        }
    }
    return counted ? sum : entry.totalAmount;
};

const CREDIT_TX_CHIP_COLOR: Record<CreditTransaction['type'], 'default' | 'primary' | 'success' | 'warning' | 'error'> = {
    issued: 'primary',
    adjusted: 'warning',
    redeemed: 'default',
    expired: 'error',
};

const CREDIT_DETAIL_COL_SPAN = 7;

const SectionLabel = ({ label, factor }: { label: string; factor: number }) => (
    <Typography
        variant="caption"
        color="primary.main"
        sx={{
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 1,
            fontSize: 10 * factor,
            width: '100%',
            display: 'block',
            textAlign: 'left',
        }}
    >
        {label}
    </Typography>
);

const StatChip = ({
    label,
    value,
    color,
    factor,
}: {
    label: string;
    value: number | string;
    color?: 'primary' | 'success' | 'default';
    factor: number;
}) => (
    <Stack direction="column" alignItems="center" spacing={0.25}>
        <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: 9 * factor, textTransform: 'uppercase', letterSpacing: 0.5 }}
        >
            {label}
        </Typography>
        <Chip
            label={value}
            size="small"
            color={color ?? 'default'}
            sx={{ fontSize: 12 * factor, fontWeight: 700 }}
        />
    </Stack>
);

const ReadOnlyRewardTransactionTable = ({ transactions, factor }: { transactions: RewardTransaction[]; factor: number }) => {
    if (!transactions.length) {
        return (
            <Typography variant="caption" color="text.secondary" sx={{ pl: 1, fontSize: 11 * factor }}>
                No transactions yet.
            </Typography>
        );
    }
    return (
        <TableContainer sx={{ overflowX: 'auto', maxWidth: '100%' }}>
            <Table size="small" sx={{ '& th, & td': { fontSize: 11 * factor } }}>
                <TableHead>
                    <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Expires</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {[...transactions]
                        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                        .map((tx) => (
                            <TableRow key={tx.transactionID} hover>
                                <TableCell>
                                    {new Date(tx.timestamp).toLocaleDateString('en-US', { timeZone: 'UTC' })}
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={tx.type}
                                        size="small"
                                        color={tx.type === 'earned' ? 'success' : tx.type === 'redeemed' ? 'warning' : 'default'}
                                        sx={{ fontSize: 10 * factor }}
                                    />
                                </TableCell>
                                <TableCell>{tx.description}</TableCell>
                                <TableCell
                                    align="right"
                                    sx={{ fontWeight: 600, color: tx.type === 'earned' ? 'success.main' : 'inherit' }}
                                >
                                    {tx.type === 'earned' ? '+' : '-'}
                                    {tx.amount}
                                </TableCell>
                                <TableCell>{formatDateUTC(tx.expiresAt)}</TableCell>
                            </TableRow>
                        ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const RewardsReadOnlyCard = ({ bucket, factor, tourCollapsible }: { bucket: RewardBucket; factor: number; tourCollapsible?: boolean }) => {
    const summaryRow = (
        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ width: '100%', justifyContent: 'flex-start' }}>
            <EmojiEventsIcon color="primary" sx={{ fontSize: 18 }} />
            {tourCollapsible ? (
                <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 12 * factor }}>
                    Rewards
                </Typography>
            ) : null}
            <StatChip label="Balance" value={bucket.currentBalance} color="primary" factor={factor} />
            <StatChip label="Earned" value={bucket.lifetimeEarned} color="success" factor={factor} />
            <StatChip label="Redeemed" value={bucket.lifetimeRedeemed} factor={factor} />
        </Stack>
    );

    const historyBlock = (
        <>
            <Divider sx={{ my: 1.5, width: '100%' }} />
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: 10 * factor, textTransform: 'uppercase', letterSpacing: 0.5, pl: 0.5 }}
            >
                Transaction History
            </Typography>
            <Box sx={{ mt: 0.5, width: '100%' }}>
                <ReadOnlyRewardTransactionTable transactions={bucket.transactions} factor={factor} />
            </Box>
        </>
    );

    if (tourCollapsible) {
        return (
            <Accordion disableGutters elevation={0} sx={ACCORDION_SX}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>{summaryRow}</AccordionSummary>
                <AccordionDetails sx={{ pt: 0, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                    {historyBlock}
                </AccordionDetails>
            </Accordion>
        );
    }

    return (
        <Accordion disableGutters elevation={0} sx={ACCORDION_SX}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack spacing={1} sx={{ width: '100%', alignItems: 'stretch' }}>
                    <SectionLabel label="Rewards" factor={factor} />
                    {summaryRow}
                </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                {historyBlock}
            </AccordionDetails>
        </Accordion>
    );
};

const DiscountsReadOnlyCard = ({
    discounts,
    factor,
    tourCollapsible,
}: {
    discounts: DiscountEntry[];
    factor: number;
    tourCollapsible?: boolean;
}) => {
    const summaryRow = (
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ width: '100%', justifyContent: 'flex-start' }}>
            <LocalOfferIcon color="primary" sx={{ fontSize: 16 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 12 * factor }}>
                Discounts
            </Typography>
            <Chip label={`${discounts.length} on record`} size="small" variant="outlined" sx={{ fontSize: 10 * factor }} />
        </Stack>
    );

    const tableOrEmpty =
        discounts.length === 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 * factor }}>
                No discounts on record.
            </Typography>
        ) : (
            <TableContainer sx={{ overflowX: 'auto', maxWidth: '100%' }}>
                <Table size="small" sx={{ '& th, & td': { fontSize: 11 * factor } }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Description</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell align="right">Value</TableCell>
                            <TableCell>Start</TableCell>
                            <TableCell>End</TableCell>
                            <TableCell sx={USAGE_LIMIT_CELL_SX}>Used / Limit</TableCell>
                            <TableCell>Req. Owner</TableCell>
                            <TableCell>Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {discounts.map((d) => (
                            <TableRow key={d.discountID} hover sx={{ opacity: d.isActive ? 1 : 0.55 }}>
                                <TableCell>{d.description}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={d.discountType === 'percentage' ? '%' : '$'}
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontSize: 10 * factor }}
                                    />
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>
                                    {d.discountType === 'percentage' ? `${d.value}%` : formatCurrency(d.value)}
                                </TableCell>
                                <TableCell>{formatDate(d.startDate)}</TableCell>
                                <TableCell>{formatDate(d.endDate)}</TableCell>
                                <TableCell sx={USAGE_LIMIT_CELL_SX}>
                                    {d.usageCount} / {d.usageLimit ?? '∞'}
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={d.requireOwner ? 'Yes' : 'No'}
                                        size="small"
                                        color={d.requireOwner ? 'warning' : 'default'}
                                        sx={{ fontSize: 10 * factor }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={d.isActive ? 'Active' : 'Archived'}
                                        size="small"
                                        color={d.isActive ? 'success' : 'default'}
                                        sx={{ fontSize: 10 * factor }}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );

    if (tourCollapsible) {
        return (
            <Accordion disableGutters elevation={0} sx={ACCORDION_SX}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>{summaryRow}</AccordionSummary>
                <AccordionDetails sx={{ pt: 0, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                    {tableOrEmpty}
                </AccordionDetails>
            </Accordion>
        );
    }

    return (
        <Accordion disableGutters elevation={0} sx={ACCORDION_SX}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack spacing={1} sx={{ width: '100%', alignItems: 'stretch' }}>
                    <SectionLabel label="Discounts" factor={factor} />
                    <Box sx={{ width: '100%' }}>{summaryRow}</Box>
                </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                {tableOrEmpty}
            </AccordionDetails>
        </Accordion>
    );
};

const CreditsReadOnlyCard = ({
    credits,
    factor,
    tourCollapsible,
}: {
    credits: CreditEntry[];
    factor: number;
    tourCollapsible?: boolean;
}) => {
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});

    const toggleExpand = useCallback((creditID: string) => {
        setExpanded((prev) => ({ ...prev, [creditID]: !prev[creditID] }));
    }, []);

    const balanceTotal = credits.filter((c) => c.isActive).reduce((sum, c) => sum + c.remainingAmount, 0);
    const issuedTotal = credits.reduce((sum, c) => sum + creditGrantedFaceTotal(c), 0);

    const summaryRow = (
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ width: '100%', justifyContent: 'flex-start' }}>
            <CreditScoreIcon color="primary" sx={{ fontSize: 16 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 12 * factor }}>
                Credits
            </Typography>
            <Chip label={`Balance: ${formatCurrency(balanceTotal)}`} size="small" color="primary" sx={{ fontSize: 11 * factor }} />
            <Chip label={`Issued: ${formatCurrency(issuedTotal)}`} size="small" color="success" sx={{ fontSize: 11 * factor }} />
        </Stack>
    );

    const tableOrEmpty =
        credits.length === 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 * factor }}>
                No credits on record.
            </Typography>
        ) : (
            <TableContainer sx={{ overflowX: 'auto', maxWidth: '100%' }}>
                <Table size="small" sx={{ '& th, & td': { fontSize: 11 * factor } }}>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox" sx={{ width: 44 }} />
                            <TableCell>Description</TableCell>
                            <TableCell align="right">Total</TableCell>
                            <TableCell align="right">Remaining</TableCell>
                            <TableCell>Issued</TableCell>
                            <TableCell>Expires</TableCell>
                            <TableCell>Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {credits.map((c) => {
                            const isOpen = !!expanded[c.creditID];
                            const txs: CreditTransaction[] = [...(c.transactions ?? [])].sort(
                                (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
                            );
                            return (
                                <Fragment key={c.creditID}>
                                    <TableRow hover>
                                        <TableCell padding="checkbox" sx={{ verticalAlign: 'middle' }}>
                                            <Tooltip
                                                title={isOpen ? 'Hide transactions & notes' : 'Show transactions & notes'}
                                            >
                                                <IconButton
                                                    size="small"
                                                    onClick={() => toggleExpand(c.creditID)}
                                                    aria-expanded={isOpen}
                                                >
                                                    {isOpen ? (
                                                        <KeyboardArrowUpIcon sx={{ fontSize: 18 }} />
                                                    ) : (
                                                        <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
                                                    )}
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>{c.description}</TableCell>
                                        <TableCell align="right">{formatCurrency(creditGrantedFaceTotal(c))}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                                            {formatCurrency(c.remainingAmount)}
                                        </TableCell>
                                        <TableCell>{formatDate(c.issuedAt)}</TableCell>
                                        <TableCell>{formatDate(c.expiresAt)}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={c.isActive ? 'Active' : 'Inactive'}
                                                size="small"
                                                color={c.isActive ? 'success' : 'default'}
                                                sx={{ fontSize: 10 * factor }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell
                                            colSpan={CREDIT_DETAIL_COL_SPAN}
                                            sx={{
                                                py: 0,
                                                borderBottom: isOpen ? undefined : 'none',
                                                backgroundColor: 'action.hover',
                                            }}
                                        >
                                            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                                <Box sx={{ py: 1.25, px: 1 }}>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{
                                                            fontSize: 10 * factor,
                                                            fontWeight: 700,
                                                            textTransform: 'uppercase',
                                                            letterSpacing: 0.8,
                                                            display: 'block',
                                                            mb: 0.75,
                                                        }}
                                                    >
                                                        Transaction history (notes from each adjustment or issue)
                                                    </Typography>
                                                    {txs.length === 0 ? (
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{ fontSize: 11 * factor }}
                                                        >
                                                            No transactions on record.
                                                        </Typography>
                                                    ) : (
                                                        <Table size="small">
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell sx={{ fontSize: 10 * factor }}>When</TableCell>
                                                                    <TableCell sx={{ fontSize: 10 * factor }}>Type</TableCell>
                                                                    <TableCell align="right" sx={{ fontSize: 10 * factor }}>
                                                                        Amount
                                                                    </TableCell>
                                                                    <TableCell sx={{ fontSize: 10 * factor }}>Note</TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {txs.map((tx) => (
                                                                    <TableRow key={tx.transactionID}>
                                                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                                            {formatDateTimeLocal(tx.timestamp)}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <Chip
                                                                                label={tx.type}
                                                                                size="small"
                                                                                color={CREDIT_TX_CHIP_COLOR[tx.type]}
                                                                                sx={{
                                                                                    fontSize: 9 * factor,
                                                                                    textTransform: 'capitalize',
                                                                                }}
                                                                            />
                                                                        </TableCell>
                                                                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                                            {formatCurrency(tx.amount)}
                                                                        </TableCell>
                                                                        <TableCell>{tx.description || '—'}</TableCell>
                                                                    </TableRow>
                                                                ))}
                                                            </TableBody>
                                                        </Table>
                                                    )}
                                                </Box>
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                </Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        );

    if (tourCollapsible) {
        return (
            <Accordion disableGutters elevation={0} sx={ACCORDION_SX}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>{summaryRow}</AccordionSummary>
                <AccordionDetails sx={{ pt: 0, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                    {tableOrEmpty}
                </AccordionDetails>
            </Accordion>
        );
    }

    return (
        <Accordion disableGutters elevation={0} sx={ACCORDION_SX}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack spacing={1} sx={{ width: '100%', alignItems: 'stretch' }}>
                    <SectionLabel label="Credits" factor={factor} />
                    <Box sx={{ width: '100%' }}>{summaryRow}</Box>
                </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                {tableOrEmpty}
            </AccordionDetails>
        </Accordion>
    );
};

type ScopeTab = 'momentGolf' | 'tour';

export const MCRewardsCreditsSection = () => {
    const { factor, userData, setUserData, windowSize } = useMCContext();
    const api = CreateApi();

    const [scopeTab, setScopeTab] = useState<ScopeTab>('momentGolf');
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);

    const f = factor < 1 ? factor : 1;

    const isMobileLayout =
        windowSize.isMobile || windowSize.verticalPC !== 'none' || ['small'].includes(windowSize.horizontalPC);

    /** Layout: wide uses full MC-style horizontal padding; narrow uses minimal outer padding. */
    const isNarrowViewport = windowSize.width < 600;

    /** Inset for Series/Tour elevated cards so box shadow clears the scroll edge (esp. left). */
    const seriesCardGutter =
        !isNarrowViewport ? { pl: 1, pr: 1, py: 0.5 } : isMobileLayout ? { pl: 1.25, pr: 1, py: 0.5 } : { pl: 0.5, pr: 1, py: 0.5 };

    const scopeTabIndex = scopeTab === 'momentGolf' ? 0 : 1;

    useEffect(() => {
        const loadProfile = async () => {
            setLoading(true);
            setLoadError(null);
            try {
                const { data } = await api.get(`/myaccount/GetOwnProfile`);
                if (data) {
                    setUserData(data);
                }
            } catch (error) {
                if (!axios.isAxiosError(error) || error.response?.status !== 404) {
                    console.error('Error loading profile for rewards & credits:', error);
                }
                setLoadError('Could not load rewards and credits. Please try again.');
            } finally {
                setLoading(false);
            }
        };
        void loadProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- CreateApi() is not stable; match UserAccountSplash pattern
    }, [setUserData]);

    const selectedUser = (userData ?? null) as MomentUser | null;

    const rewards: UserRewards = selectedUser?.rewards ?? DEFAULT_REWARDS;
    const creditsDiscounts: UserCreditsDiscounts = selectedUser?.creditsDiscounts ?? DEFAULT_CREDITS_DISCOUNTS;

    const profileTours = selectedUser?.tours ?? [];
    const tourRewardBuckets: TourRewardBucket[] = profileTours.map((t) => {
        const existing = (rewards.tour ?? []).find((b) => b.tourID === t.tourID);
        return (
            existing ?? {
                tourID: t.tourID,
                tourName: t.tourName,
                currentBalance: 0,
                lifetimeEarned: 0,
                lifetimeRedeemed: 0,
                transactions: [],
            }
        );
    });
    const tourCreditBuckets: TourCreditDiscountBucket[] = profileTours.map((t) => {
        const existing = (creditsDiscounts.tour ?? []).find((b) => b.tourID === t.tourID);
        return (
            existing ?? {
                tourID: t.tourID,
                tourName: t.tourName,
                credits: [],
                discounts: [],
            }
        );
    });

    const mgBucketReward = rewards.momentGolf ?? EMPTY_MG_BUCKET;
    const mgBucketCredit = creditsDiscounts.momentGolf ?? EMPTY_CREDIT_BUCKET;

    if (loading) {
        return (
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 200,
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                flex: 1,
                width: '100%',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                ...(isNarrowViewport ? { px: 0.5 } : { px: 2 }),
                py: 1.5,
                boxSizing: 'border-box',
            }}
        >
            <Box
                sx={{
                    flexShrink: 0,
                    mb: 1,
                    width: '100%',
                    alignSelf: 'stretch',
                    textAlign: 'left',
                    ...(isNarrowViewport ? { pl: isMobileLayout ? 1.5 : 2 } : {}),
                }}
            >
                <Typography
                    variant="subtitle1"
                    color="primary.main"
                    sx={{
                        marginLeft: 1,
                        fontWeight: 600,
                        fontSize: 14 * f,
                        textAlign: 'left',
                        width: '100%',
                        display: 'block',
                    }}
                >
                    Rewards & Credits
                </Typography>
                {loadError && (
                    <Alert severity="error" sx={{ mt: 1.5 }}>
                        {loadError}
                    </Alert>
                )}
            </Box>

            <Tabs
                value={scopeTabIndex}
                onChange={(_, v) => {
                    setScopeTab(v === 0 ? 'momentGolf' : 'tour');
                }}
                variant="scrollable"
                scrollButtons={false}
                TabIndicatorProps={{ sx: { display: 'none' } }}
                aria-label="Rewards and credits scope"
                sx={{
                    flexShrink: 0,
                    minHeight: 0,
                    alignSelf: 'stretch',
                    maxWidth: '100%',
                    mb: 1,
                    ...(isNarrowViewport && isMobileLayout ? { pl: 1.5 } : {}),
                    '& .MuiTabs-flexContainer': {
                        gap: 1,
                        flexWrap: 'wrap',
                        width: '100%',
                    },
                    '& .MuiTabs-scroller': { overflow: 'auto !important' },
                    '& .MuiTab-root': {
                        minHeight: 32,
                        flex: isMobileLayout ? 1 : '0 0 auto',
                        width: isMobileLayout ? undefined : 'max-content',
                        minWidth: isMobileLayout ? 0 : undefined,
                        py: 0.5,
                        px: isMobileLayout ? 1 : 2,
                        borderRadius: '16px',
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: isMobileLayout ? '0.75rem' : '0.875rem',
                        justifyContent: isMobileLayout ? 'center' : undefined,
                        '& .MuiSvgIcon-root': {
                            color: 'primary.dark',
                        },
                        '&.Mui-selected': {
                            backgroundColor: 'primary.extraLight',
                            color: 'primary.dark',
                            '& .MuiSvgIcon-root': {
                                color: 'primary.dark',
                            },
                        },
                    },
                }}
            >
                <Tab label="Moment Golf" id="rewards-credits-tab-moment-golf" aria-controls="rewards-credits-panel" />
                <Tab label="Series / Tour" id="rewards-credits-tab-tour" aria-controls="rewards-credits-panel" />
            </Tabs>

            <Box
                sx={{
                    flex: '1 1 0',
                    height: 0,
                    minHeight: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    width: '100%',
                    alignSelf: 'stretch',
                }}
            >
                <Box
                    id="rewards-credits-panel"
                    role="tabpanel"
                    aria-labelledby={
                        scopeTab === 'momentGolf'
                            ? 'rewards-credits-tab-moment-golf'
                            : 'rewards-credits-tab-tour'
                    }
                    sx={{ width: '100%', pb: 2, boxSizing: 'border-box' }}
                >
                    {scopeTab === 'momentGolf' && (
                        <Stack
                            spacing={2}
                            sx={{
                                width: '100%',
                                boxSizing: 'border-box',
                                ...(isMobileLayout ? { px: 1 } : {}),
                            }}
                        >
                            <RewardsReadOnlyCard bucket={mgBucketReward} factor={f} />
                            <DiscountsReadOnlyCard discounts={mgBucketCredit.discounts} factor={f} />
                            <CreditsReadOnlyCard credits={mgBucketCredit.credits} factor={f} />
                        </Stack>
                    )}

                    {scopeTab === 'tour' &&
                        (profileTours.length === 0 ? (
                            <Paper variant="outlined" sx={{ ...CARD_SX, p: 2 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 * f }}>
                                    No series associations on record.
                                </Typography>
                            </Paper>
                        ) : (
                            <Stack spacing={2.5} sx={{ width: '100%', boxSizing: 'border-box', ...seriesCardGutter }}>
                                {tourRewardBuckets.map((rb) => {
                                    const cb = tourCreditBuckets.find((b) => b.tourID === rb.tourID);
                                    if (!cb) {
                                        return null;
                                    }
                                    return (
                                        <Paper
                                            key={rb.tourID}
                                            elevation={3}
                                            sx={{
                                                p: 1.5,
                                                borderRadius: 2,
                                                width: '100%',
                                                maxWidth: '100%',
                                                boxSizing: 'border-box',
                                                textAlign: 'left',
                                            }}
                                        >
                                            <Typography
                                                variant="subtitle2"
                                                color="primary.main"
                                                sx={{
                                                    fontWeight: 600,
                                                    fontSize: 13 * f,
                                                    mb: 1.5,
                                                    textAlign: 'left',
                                                    width: '100%',
                                                    display: 'block',
                                                }}
                                            >
                                                {rb.tourName || rb.tourID}
                                            </Typography>
                                            <Stack spacing={2} sx={{ alignItems: 'stretch', width: '100%' }}>
                                                <RewardsReadOnlyCard bucket={rb} factor={f} tourCollapsible />
                                                <DiscountsReadOnlyCard discounts={cb.discounts} factor={f} tourCollapsible />
                                                <CreditsReadOnlyCard credits={cb.credits} factor={f} tourCollapsible />
                                            </Stack>
                                        </Paper>
                                    );
                                })}
                            </Stack>
                        ))}
                </Box>
            </Box>
        </Box>
    );
};
